/**
 * SafetyMeetingModal — daily health & safety video shown on the first clock-in
 * of the day. Sound on by default (with a mute toggle). CONFIRM logs the
 * acknowledgement (time, IP, device location) and continues the clock-in;
 * DENY logs a deny and restarts the video. Confirm/deny are available at any
 * point — nobody is forced to wait for the end of the video.
 */
import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, ShieldCheck, Loader2 } from "lucide-react";
import SafetyQuizPanel from "./SafetyQuizPanel";
import api from "@/lib/api";
import { postOrQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";

const getLocation = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve({ location_error: "geolocation unsupported" });
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy }),
    (e) => resolve({ location_error: e.message || "denied" }),
    { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 },
  );
});

export default function SafetyMeetingModal({ open, onConfirmed, onClose, slotKey = "default", projectId = null, videoVersion, quiz = false }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(0);
  const [quizStep, setQuizStep] = useState(false);
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/safety/video?slot=${encodeURIComponent(slotKey)}&v=${videoVersion || 0}`;

  useEffect(() => { if (!open) { setPlaying(false); setDenied(0); setQuizStep(false); } }, [open]);
  if (!open) return null;

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { try { await v.play(); setPlaying(true); } catch { toast.error("Could not start the video"); } }
    else { v.pause(); setPlaying(false); }
  };
  const payload = async (action) => ({ action, slot_key: slotKey, project_id: projectId, tz_offset_min: new Date().getTimezoneOffset(), watched_seconds: videoRef.current?.currentTime || 0, ...(await getLocation()) });

  const recordConfirm = async () => {
    setBusy(true);
    try {
      videoRef.current?.pause();
      const res = await postOrQueue("/safety/log", await payload("confirm"), "safety confirmation");
      if (res.queued) toast.warning("No signal — your confirmation is saved and will be sent when you're back online");
      onConfirmed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record your confirmation");
      setQuizStep(false);
    } finally { setBusy(false); }
  };
  // With the quiz on, CONFIRM first opens the questions; the confirmation is only recorded once they're all right.
  const confirm = () => { if (quiz) { videoRef.current?.pause(); setQuizStep(true); } else recordConfirm(); };
  const restartVideo = async () => {
    const v = videoRef.current;
    if (v) { v.currentTime = 0; try { await v.play(); setPlaying(true); } catch { setPlaying(false); } }
  };
  const deny = async () => {
    setBusy(true);
    try { await api.post("/safety/log", await payload("deny")); } catch { /* still restart */ }
    setBusy(false);
    setDenied((n) => n + 1);
    await restartVideo();
    toast.message("Video restarted — please watch it through, then confirm.");
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 p-3 backdrop-blur-sm" data-testid="safety-meeting-modal" role="dialog" aria-modal="true">
      <div className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0b1220] text-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
          <ShieldCheck className="h-5 w-5 text-emerald-400" />
          <div className="min-w-0">
            <div className="text-sm font-bold">Daily safety meeting</div>
            <div className="text-[11px] text-white/55">Watch today's health &amp; safety video before you clock in.</div>
          </div>
          <button onClick={() => setMuted((m) => !m)} aria-label={muted ? "Sound off — turn on" : "Sound on — turn off"} title={muted ? "Sound off" : "Sound on"}
            className={`ml-auto rounded-md border px-2.5 py-1.5 text-xs font-semibold ${muted ? "border-red-400/40 text-red-300" : "border-emerald-400/40 text-emerald-300"}`} data-testid="safety-sound-toggle">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>

        <div className="relative bg-black">
          <video ref={videoRef} src={src} muted={muted} playsInline preload="auto" controls={false}
            onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)}
            className="aspect-video w-full" data-testid="safety-video" />
          {!playing && (
            <button onClick={togglePlay} aria-label="Play video" className="absolute inset-0 flex items-center justify-center bg-black/40" data-testid="safety-play">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 text-white shadow-xl transition-transform hover:scale-105">
                <Play className="ml-1 h-9 w-9" />
              </span>
            </button>
          )}
          {playing && (
            <button onClick={togglePlay} aria-label="Pause video" className="absolute bottom-3 right-3 rounded-full bg-black/60 p-2 text-white" data-testid="safety-pause">
              <Pause className="h-4 w-4" />
            </button>
          )}
        </div>

        {quizStep ? (
          <div className="px-4 py-4">
            <SafetyQuizPanel slotKey={slotKey} onPassed={recordConfirm} onFailed={async () => { setQuizStep(false); setDenied((n) => n + 1); await restartVideo(); }} />
            {onClose && <button onClick={onClose} className="mx-auto mt-3 block text-[11px] text-white/40 underline" data-testid="safety-cancel">Cancel clock-in</button>}
          </div>
        ) : (
        <div className="space-y-3 px-4 py-4">
          <p className="text-center text-sm font-semibold" data-testid="safety-question">I confirm I have watched this entire video and understand it.</p>
          {denied > 0 && <p className="text-center text-[11px] text-amber-300">The video restarted. Watch it fully, then confirm{quiz ? " and answer the questions" : ""}.</p>}
          <div className="grid grid-cols-2 gap-3">
            <button onClick={deny} disabled={busy} className="rounded-xl border border-red-400/50 bg-red-500/10 py-3 text-sm font-bold uppercase tracking-widest text-red-300 hover:bg-red-500/20 disabled:opacity-50" data-testid="safety-deny">Deny</button>
            <button onClick={confirm} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-bold uppercase tracking-widest text-white hover:bg-emerald-500 disabled:opacity-50" data-testid="safety-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Confirm
            </button>
          </div>
          <p className="text-center text-[10px] text-white/40">Your confirmation is logged with the time, IP address and device location.</p>
          {onClose && <button onClick={onClose} className="mx-auto block text-[11px] text-white/40 underline" data-testid="safety-cancel">Cancel clock-in</button>}
        </div>
        )}
      </div>
    </div>
  );
}
